import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, CircularProgress,
  Autocomplete, Checkbox, Chip, Stack, Snackbar, Alert, InputAdornment
} from '@mui/material';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Search, TrendingUp, Calendar, Users, BarChart3, X } from 'lucide-react';
import api from '../services/api';

function ymd(d) {
  const dt = d instanceof Date ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const PALETTE = [
  '#1f77b4','#ff7f0e','#2ca02c','#d62728','#9467bd',
  '#8c564b','#e377c2','#7f7f7f','#bcbd22','#17becf',
  '#393b79','#637939','#8c6d31','#843c39','#7b4173',
  '#3182bd','#e6550d','#31a354','#756bb1','#636363'
];

export default function HourlyRateHistoryPage() {
  const [allEmployees, setAllEmployees] = useState([]);          // [{id, name}]
  const [selectedEmployees, setSelectedEmployees] = useState([]); // [] by default
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Date range (default last 90 days)
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return ymd(d);
  });
  const [endDate, setEndDate] = useState(() => ymd(new Date()));

  // Raw per-employee daily data: { [id]: { name, daily: [{date, hourlyRate, commissionPercent}] } }
  const [byEmployee, setByEmployee] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/employees_simple_list'); // [{id, name}]
        setAllEmployees(res.data || []);
      } catch {
        setSnackbar({ open: true, severity: 'error', message: 'Failed to load employees' });
      }
    })();
  }, []);

  // Stable color per employee id
  const colorMap = useMemo(() => {
    const map = new Map();
    allEmployees.forEach((e, idx) => {
      map.set(String(e.id), PALETTE[idx % PALETTE.length]);
    });
    return map;
  }, [allEmployees]);

  const fetchData = async () => {
    if (!selectedEmployees.length) {
      setSnackbar({ open: true, severity: 'warning', message: 'Select at least one employee' });
      return;
    }
    setLoading(true);
    try {
      const entries = await Promise.all(
        selectedEmployees.map(async (emp) => {
          const url = `/salary/${emp.id}/details?start_date=${startDate}&end_date=${endDate}`;
          const r = await api.get(url);
          const daily = (r.data?.dailyData || []).map(row => ({
            date: row.date,
            hourlyRate: Number(row.hourlyRate ?? 0),
            commissionPercent: Number(row.commissionPercent ?? 0)
          }));
          return [String(emp.id), { name: emp.name, daily }];
        })
      );
      setByEmployee(Object.fromEntries(entries));
      setSnackbar({ open: true, severity: 'success', message: 'Data loaded successfully' });
    } catch {
      setSnackbar({ open: true, severity: 'error', message: 'Error loading data' });
    } finally {
      setLoading(false);
    }
  };

  // Build chart data for hourly rate: rows with all dates merged
  const chartDataHourly = useMemo(() => {
    const dateSet = new Set();
    Object.values(byEmployee).forEach(emp => {
      emp.daily.forEach(d => dateSet.add(d.date));
    });
    const dates = Array.from(dateSet).sort();
    return dates.map(date => {
      const row = { date };
      Object.entries(byEmployee).forEach(([id, emp]) => {
        const found = emp.daily.find(d => d.date === date);
        row[`emp_${id}`] = found ? found.hourlyRate : null;
      });
      return row;
    });
  }, [byEmployee]);

  // Build chart data for commission percent
  const chartDataCommission = useMemo(() => {
    const dateSet = new Set();
    Object.values(byEmployee).forEach(emp => {
      emp.daily.forEach(d => dateSet.add(d.date));
    });
    const dates = Array.from(dateSet).sort();
    return dates.map(date => {
      const row = { date };
      Object.entries(byEmployee).forEach(([id, emp]) => {
        const found = emp.daily.find(d => d.date === date);
        row[`emp_${id}`] = found ? found.commissionPercent : null;
      });
      return row;
    });
  }, [byEmployee]);

  const lines = useMemo(() => {
    return Object.entries(byEmployee).map(([id, emp]) => ({
      id,
      name: emp.name,
      key: `emp_${id}`,
      color: colorMap.get(id) || '#000'
    }));
  }, [byEmployee, colorMap]);

  const clearSelection = () => setSelectedEmployees([]);

  return (
    <Box p={1}>
      {/* Filter Box */}
      <Box sx={{ 
        overflowX: 'auto', 
        borderRadius: 2, 
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)', 
        maxWidth: 1000, 
        margin: '0 auto 16px auto', 
        width: '100%',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { height: '8px'},
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px'}
      }}>
        <Paper sx={{ 
          backgroundColor: '#fff', 
          p: 2,
          borderRadius: 2
        }}>
          <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ 
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Users size={20} color="#666" />
            Filter Options
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={allEmployees}
              value={selectedEmployees}
              onChange={(e, v) => setSelectedEmployees(v)}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} />
                  {option.name} (ID: {option.id})
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    sx={{
                      bgcolor: colorMap.get(String(option.id)) || '#1976d2',
                      color: '#fff',
                      fontWeight: 'bold'
                    }}
                  />
                ))
              }
              sx={{ flex: 1, minWidth: 300 }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  size="small" 
                  placeholder="Select employees to analyze"
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#ddd',
                      },
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Search size={18} color="#666" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <TextField
              type="date"
              size="small"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sx={{ 
                width: { xs: '100%', sm: 180 },
                backgroundColor: '#fff',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#ddd',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Calendar size={18} color="#666" />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            
            <TextField
              type="date"
              size="small"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sx={{ 
                width: { xs: '100%', sm: 180 },
                backgroundColor: '#fff',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#ddd',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Calendar size={18} color="#666" />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />

            <Stack direction="row" spacing={1} sx={{ 
              minWidth: { xs: '100%', sm: 'auto' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button 
                variant="outlined" 
                onClick={clearSelection}
                startIcon={<X size={18} />}
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #f5f5f5',


                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                    border: '1px solid #bbb',
                  },
                  flex: 1
                }}
              >
                Clear
              </Button>
              
              <Button 
                variant="contained" 
                onClick={fetchData}
                startIcon={<BarChart3 size={18} />}
                disabled={loading}
                sx={{
                  fontWeight: 'bold',
                  background: 'linear-gradient(to right, #1976d2, #0d47a1)',
                  color: '#fff',
                  '&:hover': {
                    background: 'linear-gradient(to right, #1565c0, #003c8f)',
                  },
                  '&:disabled': {
                    background: '#ccc',
                    color: '#666',
                  },
                  flex: 1
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Load'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>

      {/* Charts Section */}
      <Box sx={{ 
        overflowX: 'auto', 
        borderRadius: 2, 
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)', 
        maxWidth: 1000, 
        margin: '0 auto', 
        width: '100%',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { height: '8px'},
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px'}
      }}>
        <Paper sx={{ 
          backgroundColor: '#fff', 
          p: 2,
          borderRadius: 2,
          minHeight: 480
        }}>
          {loading ? (
            <Box sx={{ 
              py: 8, 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}>
              <CircularProgress size={40} />
              <Typography color="text.secondary">Loading hourly rate data...</Typography>
            </Box>
          ) : !lines.length ? (
            <Box sx={{ 
              py: 8, 
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}>
              <BarChart3 size={48} color="#ccc" />
              <Typography variant="h6" color="text.secondary" fontWeight="bold">
                No Data Selected
              </Typography>
              <Typography color="text.secondary">
                Select employees and click "Load Data" to view hourly rate & commission history.
              </Typography>
            </Box>
          ) : chartDataHourly.length === 0 ? (
            <Box sx={{ 
              py: 8, 
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}>
              <BarChart3 size={48} color="#ccc" />
              <Typography variant="h6" color="text.secondary" fontWeight="bold">
                No Data Available
              </Typography>
              <Typography color="text.secondary">
                No hourly rate data found for the selected date range.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Hourly Rate Chart */}
              <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ 
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <TrendingUp size={20} color="#666" />
                Hourly Rate Trends
              </Typography>
              
              <Box sx={{ width: '100%', height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartDataHourly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#666"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#666"
                      fontSize={12}
                      tickFormatter={(value) => `$${value}`}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',

                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                      formatter={(value, name) => [`$${value?.toFixed(2) || '0.00'}`, name]}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Legend />
                    {lines.map((l) => (
                      <Line
                        key={`rate_${l.id}`}
                        type="monotone"
                        dataKey={l.key}
                        name={`${l.name} - $/h`}
                        stroke={l.color}
                        strokeWidth={3}
                        dot={{ fill: l.color, strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, stroke: l.color, strokeWidth: 2 }}
                        isAnimationActive={false}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              {/* Commission Percent Chart */}
              <Box sx={{ width: '100%', height: 320, mt: 4 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ 
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <TrendingUp size={20} color="#666" />
                  Commission Percent Trends
                </Typography>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartDataCommission}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#666"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#666"
                      fontSize={12}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',

                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                      formatter={(value, name) => [`${value?.toFixed(2) || '0.00'}%`, name]}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Legend />
                    {lines.map((l) => (
                      <Line
                        key={`comm_${l.id}`}
                        type="monotone"
                        dataKey={l.key}
                        name={`${l.name} - %`}
                        stroke={l.color}
                        strokeWidth={3}
                        dot={{ fill: l.color, strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, stroke: l.color, strokeWidth: 2 }}
                        isAnimationActive={false}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </>
          )}
        </Paper>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(s => ({ ...s, open: false }))} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            borderRadius: 2,
            fontWeight: 'bold'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
